import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import {
    resetToSystemDefault,
    resetToUserDefault,
    setAppStateSliceKey,
    setDeserializedAppState,
} from 'Angular/state/app-state/app-state.actions';

import { LooseObject } from 'Angular/interfaces/loose-object.interface';
import { QueryParams } from 'Angular/state/app-state/interfaces/query-params.interface';

import equal from 'Angular/utils/equal/deep-equal';
import { convertStringToProperType, formFlattenedObject, parseFlattenedObject } from 'Angular/utils/urls';

/**
 * App State Service
 *
 */

@Injectable({
    providedIn: 'root',
})
export class AppStateService implements OnDestroy {
    // Set by each app
    stateSliceKey: string = '';

    urlParamKeys: string[] = [];

    currentSerializedState: LooseObject = {};

    customSerializer: any = {};

    customDeserializer: any = {};

    // A guided workflow ID may be passed in the URL to specify a workflow to open in the side menu when the page loads.
    private readonly reservedGuidedWorkflowKey = 'gw';

    // Query params that add functionality to the app but are not part of the apps state logic.
    private readonly statelessQueryParams = [this.reservedGuidedWorkflowKey];

    // Set to true after the state has been set initially from URL params or user preferences
    enabled: boolean = false;

    // Use replaceUrl: true for next updates instead of adding to the history stack
    private replaceNextUrlUpdateFlag: boolean = false;

    // Replaces the history entry on the first URL sync after start() instead of pushing a new entry
    private replaceInitialUrlSync: boolean = false;

    private hasPerformedInitialUrlSync: boolean = false;

    storeSubscription: Subscription | null = null;

    queryParamMapSubscription: Subscription | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private readonly store: Store,
        private readonly locationStrategy: LocationStrategy
    ) {}

    // Whether the part of the URL the router actually parses contains query params. Apps using
    // hash routing only read params from the fragment (/page/#/?tab=x), so params injected before
    // the '#' by third parties (e.g. Google's ?_gl=... link decoration) must be ignored — the
    // router can never see them, and treating them as app state leaves the app uninitialized.
    private routerVisibleUrlHasQueryParams(): boolean {
        const routerVisibleUrl =
            this.locationStrategy instanceof HashLocationStrategy ? window.location.hash : window.location.search;
        return routerVisibleUrl.includes('?');
    }

    public serialize(state: LooseObject): { [k: string]: string | boolean | number } {
        return formFlattenedObject(state, this.urlParamKeys, this.customSerializer);
    }

    public deserialize(urlParams: LooseObject): { [k: string]: any } {
        return parseFlattenedObject(urlParams, this.urlParamKeys, this.customDeserializer);
    }

    public setStateSlice(sliceKey = ''): void {
        this.stateSliceKey = sliceKey;
    }

    public setUrlParamKeys(urlKeys: string[]): void {
        this.urlParamKeys = urlKeys;
    }

    // Call this right before doing something that updates state from a deep link, so the resulting
    // URL change replaces the deep link URL instead of stacking on top of it. Back then skips it
    public replaceNextUrlUpdate(): void {
        this.replaceNextUrlUpdateFlag = true;
    }

    // Turn this on in an app's init when landing on it shouldn't leave a separate Back target
    // (e.g. a page reached by an action link). It only affects the first URL write after start()
    public setReplaceInitialUrlSync(value: boolean): void {
        this.replaceInitialUrlSync = value;
    }

    public setCustomUrlParamDeserializer(deserializer: any): void {
        this.customDeserializer = deserializer;
    }

    public setCustomUrlParamSerializer(serializer: any): void {
        this.customSerializer = serializer;
    }

    public resetToSystemDefaultState(): void {
        this.store.dispatch(resetToSystemDefault());
    }

    public start(): void {
        this.unsubscribe();
        this.hasPerformedInitialUrlSync = false;

        this.store.dispatch(setAppStateSliceKey({ stateSliceKey: this.stateSliceKey }));

        let currentUrlParams = this.serialize({});

        // Subscribe to the NgRx store, and change the URL whenever it changes in a relevant way
        this.storeSubscription = this.store
            .select((state: LooseObject) => state.reducers[this.stateSliceKey])
            .subscribe((data) => {
                /*
                 * We only allow the URL to be updated after the state has already been updated at least once.
                 * Since the store is always initialized with default values on page load,
                 * If we allow the URL to be updated on page load before the store,
                 * any query params entered by the user could be overwritten by the initial store values.
                 */
                if (!this.enabled) return;

                const lastUrlParams = currentUrlParams;
                currentUrlParams = this.serialize(data);
                this.currentSerializedState = currentUrlParams;

                let paramHasChanged = false;
                /*
                 * Compare the current URL params with what we have in the state, if there's a discrepancy we should switch
                 * paramHasChanged to true so that we trigger an update in the location, then the normal pipeline that
                 * validates the default params will run.
                 * This is mostly to prevent the case when the user removes any required param from the URL and the param
                 * gets lost, this makes so we make sure the param gets added back
                 */
                const { queryParamMap } = this.route.snapshot;
                for (let i = 0; i < this.urlParamKeys.length; i += 1) {
                    const key = this.urlParamKeys[i];
                    if (queryParamMap.get(key) === null || lastUrlParams[key] !== currentUrlParams[key]) {
                        paramHasChanged = true;
                        break;
                    }
                }
                if (paramHasChanged) {
                    // Replace the history entry (instead of adding one) if either flag asks for it:
                    // see setReplaceInitialUrlSync() and replaceNextUrlUpdate()
                    const replaceUrl =
                        this.replaceNextUrlUpdateFlag ||
                        (this.replaceInitialUrlSync && !this.hasPerformedInitialUrlSync);
                    this.hasPerformedInitialUrlSync = true;
                    this.replaceNextUrlUpdateFlag = false;
                    this.router.navigate(['.'], {
                        queryParams: currentUrlParams,
                        queryParamsHandling: 'merge',
                        replaceUrl,
                    });
                }
            });

        // Whenever the url query params change, check if we should dispatch either the resetToUserDefault or
        // setDeserializedAppState actions
        this.queryParamMapSubscription = this.route.queryParamMap.subscribe((data: ParamMap) => {
            // Cast to a QueryParams object which has a `params` attribute
            const urlParams = (<QueryParams>data).params;
            // The line above returns the params as all string values even when
            // they are a different type in the state, so we need to convert
            // the params to the proper type in order to compare them to the currentSerializedState
            const convertedUrlParams: LooseObject = {};
            const statelessUrlParamKeys: string[] = [];
            Object.entries(urlParams).forEach(([key, value]) => {
                if (this.statelessQueryParams.includes(key)) {
                    statelessUrlParamKeys.push(key);
                    return;
                }
                convertedUrlParams[key] = convertStringToProperType(value);
            });

            // If the user has not provided any app state in the URL then we reset
            // to the users default preferences. However, the data parameter to this function
            // will always be empty the first time the function is executed even if there are
            // params present in the URL, and the reason is an implementation detail of Angular.
            // Here's the related github issue where workarounds are discussed: https://github.com/angular/angular/issues/12157
            // To avoid always resetting to user default on the initial execution of this function, we also check
            // if the router-visible part of the URL does not contain a '?' character. If '?' is present then we
            // know the user has provided app state in the URL, so we should use that app state and not reset to
            // user default.

            // There's a special case of params that when being the only params present should dispatch
            // 'resetToUserDefault' as these do not affect app state but do trigger other logic in the app.
            const isStatelessQueryParamsOnly =
                statelessUrlParamKeys.length > 0 &&
                Object.keys(urlParams).every((key) => statelessUrlParamKeys.includes(key));
            if (
                (equal(convertedUrlParams, {}) && !this.routerVisibleUrlHasQueryParams()) ||
                isStatelessQueryParamsOnly
            ) {
                this.enabled = true;
                this.store.dispatch(resetToUserDefault());
            } else if (!equal(convertedUrlParams, this.currentSerializedState)) {
                this.enabled = true;

                this.store.dispatch(
                    setDeserializedAppState({ deserializedAppState: this.deserialize(convertedUrlParams) })
                );
            }
        });
    }

    stop(): void {
        this.unsubscribe();
        this.enabled = false;
        this.currentSerializedState = {};
    }

    unsubscribe(): void {
        if (this.storeSubscription) this.storeSubscription.unsubscribe();
        if (this.queryParamMapSubscription) this.queryParamMapSubscription.unsubscribe();
    }

    ngOnDestroy(): void {
        this.stop();
    }
}
